import { Directive, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, debounceTime, fromEvent, takeUntil } from 'rxjs';

@Directive({
  selector: '[nrcInfiniteScroll]',
  standalone: true
})
export class InfiniteScrollDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  @Input() public threshold = 200;
  @Input() public debounceTime = 100;
  @Input() public isInfiniteScrollDisabled = false;

  @Output() public scrolledToBottom = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();

  /**
   * Initialize the infinite scroll directive by setting up scroll event listeners.
   */
  public ngOnInit(): void {
    if (!this.elementRef.nativeElement) {
      return;
    }

    fromEvent(this.elementRef.nativeElement, 'scroll').pipe(
      debounceTime(this.debounceTime),
      takeUntil(this.destroy$)
    ).subscribe(() => this.onScroll());
  }

  /**
   * Clean up resources when the directive is destroyed.
   */
  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle scroll events and emit scrolledToBottom when user scrolls near the bottom.
   */
  private onScroll(): void {
    if (this.isInfiniteScrollDisabled) {
      return;
    }

    const element = this.elementRef.nativeElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    // Check if user scrolled close to bottom
    if (scrollTop + clientHeight >= scrollHeight - this.threshold) {
      this.scrolledToBottom.emit();
    }
  }
}
